import { useMemo } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { sample } from 'lodash';
import { bodyWidth, sharedColors } from '../../utils/Style';
import { WishlistItemPlaceholder } from '../Wishlist/WishlistItemPlaceholder';
import { useIsMobile } from '../../hooks/useIsMobile';
import { BasketTable } from './BasketTable';
import { formatCurrency } from '../../utils/Currency';
import { useThemeColor } from '../../hooks/useThemeColor';
import { isTokenValid } from '../../utils/Auth';
import { BasketRegisterAlert } from './BasketRegisterAlert';
import { ProductsCarousel } from '../../components/ProductsCarousel/ProductsCarousel';
import { BasketCard } from './BasketCard';
import { useBasket } from '../../hooks/useBasket';
import { GenericHelmet } from '../../components/Helmet/GenericHelmet';
import { useAnalytics } from '../../analytics/useAnalytics';
import { OfferCard } from '../../components/Offer/OfferCard';
import { AtCheckoutDiscountCard } from './AtCheckoutDiscountCard';
import { useLocalizedText } from '../../hooks/useLocalizedText';
import { EligibleFreeProductsCard } from './EligibleFreeProductsCard';

export const BasketPage = () => {
  const t = useLocalizedText();

  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { initiateCheckout } = useAnalytics();

  const { primary, secondary } = useThemeColor();

  const { basket, loading, refreshBasket, totalAmount, totalQuantity } =
    useBasket();

  const randomProduct = useMemo(
    () => (basket ? sample(basket.items) : undefined),
    [basket],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <GenericHelmet title={t('basket.my_basket')} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: bodyWidth,
          py: 3,
          px: 1.5,
          gap: 1.5,
          boxSizing: 'border-box',
        }}
      >
        {!isTokenValid() && <BasketRegisterAlert />}

        {!!basket?.offerSummary && (
          <OfferCard offer={basket.offerSummary} applied />
        )}

        <Typography sx={{ fontSize: '22px', fontWeight: 700 }}>
          {t('basket.my_basket')}{' '}
          {basket ? t('basket.n_products', { n: totalQuantity }) : ''}
        </Typography>

        {!!basket?.eligibleFreeProducts.length && (
          <EligibleFreeProductsCard products={basket.eligibleFreeProducts} />
        )}

        {!!basket?.atCheckoutDiscountProducts.length && (
          <AtCheckoutDiscountCard
            products={basket.atCheckoutDiscountProducts}
          />
        )}

        {loading &&
          Array(5)
            .fill(null)
            .map((_, i) => <WishlistItemPlaceholder key={i} />)}

        {!loading && !basket?.items.length && (
          <Typography>{t('basket.no_products')}</Typography>
        )}

        {!!basket?.items.length && !isMobile && (
          <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2.5 }}>
            <Box sx={{ flex: '1 1 0' }}>
              <BasketTable basket={basket} onUpdate={refreshBasket} />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                alignItems: 'stretch',
                width: 280,
              }}
            >
              <Paper
                elevation={3}
                sx={{
                  p: 2.5,
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Typography sx={{ fontWeight: 600, fontSize: '16px' }}>
                  {t('basket.order_summary')}
                </Typography>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography sx={{ fontSize: '14px' }}>
                    {t('basket.products_total')}
                  </Typography>

                  <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>
                    {formatCurrency(totalAmount)}
                  </Typography>
                </Box>
              </Paper>

              <Button
                variant='contained'
                onClick={() => {
                  initiateCheckout();
                  navigate('/checkout');
                }}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '14px',
                  color: secondary,
                  backgroundColor: primary,
                }}
              >
                {t('basket.confirm_basket')}
              </Button>
            </Box>
          </Box>
        )}

        {!!basket?.items.length && isMobile && (
          <Box
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              width: '100%',
              zIndex: 1300, // ensure it's above other content if needed
              boxShadow: 3,
              backgroundColor: sharedColors.white,
              boxSizing: 'border-box',
              py: 2,
              px: 3,
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography>{t('basket.total')}</Typography>

              <Typography sx={{ fontWeight: 600 }}>
                {formatCurrency(totalAmount)}
              </Typography>
            </Box>

            <Button
              variant='contained'
              onClick={() => {
                initiateCheckout();
                navigate('/checkout');
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                color: secondary,
                backgroundColor: primary,
              }}
            >
              {t('basket.confirm_basket')}
            </Button>
          </Box>
        )}

        {!!basket?.items.length &&
          isMobile &&
          basket.items.map((item) => (
            <BasketCard key={item.id} item={item} onUpdate={refreshBasket} />
          ))}

        {!!randomProduct && (
          <ProductsCarousel
            title={t('basket.related_products')}
            relatedProductID={randomProduct.product.id}
          />
        )}
      </Box>
    </Box>
  );
};
